import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PlanRoleBadges } from "@/components/PlanRoleBadges";
import { accountLabel } from "@/lib/accountLabel";
import {
  orgCreateInvitation,
  orgListInvitations,
  orgRevokeInvitation,
  orgResendInvitation,
  orgListMembers,
  orgSetMemberPermission,
  orgDecouple,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, UserPlus, Trash2, Users, Mail, Eye, Unlink, ChevronRight, ChevronDown, Search, Clock, Send } from "lucide-react";
import { toast } from "sonner";

const ROLE_OPTIONS = {
  steuerberater: [
    { value: "unternehmer", label: "Mandant (Unternehmer)" },
    { value: "kanzlei_mitarbeiter", label: "Kanzlei-Mitarbeiter" },
  ],
  kanzlei_mitarbeiter: [
    { value: "unternehmer", label: "Mandant (Unternehmer)" },
    { value: "kanzlei_mitarbeiter", label: "Kanzlei-Mitarbeiter" },
  ],
  unternehmer: [{ value: "mitarbeiter", label: "Mitarbeiter" }],
  mitarbeiter: [{ value: "mitarbeiter", label: "Mitarbeiter" }],
};

export default function Organization() {
  const { user, openAccount, refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ansicht = searchParams.get("ansicht"); // "mandanten" | "mitarbeiter" | null
  const isAdminMember = (user?.permissions || []).includes("admin");
  const isKanzleiAdmin = user?.account_type === "kanzlei_mitarbeiter" && isAdminMember;
  // Einladbare Rollen: Kanzlei-Mitarbeiter & Mitarbeiter nur mit „admin"-Recht.
  const baseOptions =
    (user?.account_type === "kanzlei_mitarbeiter" || user?.account_type === "mitarbeiter") &&
    !isAdminMember
      ? []
      : ROLE_OPTIONS[user?.account_type] || [];
  // Ansicht filtert die einladbaren Rollen (Mandanten vs. Mitarbeiter).
  const options = baseOptions.filter((o) => {
    if (ansicht === "mandanten") return o.value === "unternehmer";
    if (ansicht === "mitarbeiter")
      return o.value === "kanzlei_mitarbeiter" || o.value === "mitarbeiter";
    return true;
  });
  const canInvite = options.length > 0;
  const isKanzlei =
    user?.account_type === "steuerberater" || user?.account_type === "kanzlei_mitarbeiter";
  const isUnternehmer = user?.account_type === "unternehmer";
  const isViewer = user?.account_type === "mitarbeiter"; // Mitarbeiter mit Querberechtigung
  const isOwner = !!user?.is_owner;
  const isCoupled = isUnternehmer && !!user?.parent_id;

  const [email, setEmail] = useState("");
  const [role, setRole] = useState(options[0]?.value || "");
  const [busy, setBusy] = useState(false);
  const [invites, setInvites] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openingId, setOpeningId] = useState(null);
  const [resendId, setResendId] = useState(null);
  const [decoupling, setDecoupling] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    try {
      const tasks = [orgListMembers()];
      if (canInvite) tasks.push(orgListInvitations());
      const [mem, inv] = await Promise.all(tasks);
      setMembers(mem || []);
      setInvites(inv || []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [canInvite]);

  useEffect(() => {
    load();
  }, [load]);

  // Beim Wechsel der Ansicht (Mandanten/Mitarbeiter) die vorausgewählte Rolle anpassen.
  useEffect(() => {
    setRole(options[0]?.value || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ansicht]);

  // Mitglieder nach Ansicht filtern.
  const filteredMembers = members.filter((m) => {
    if (ansicht === "mandanten") return m.account_type === "unternehmer";
    if (ansicht === "mitarbeiter")
      return m.account_type === "mitarbeiter" || m.account_type === "kanzlei_mitarbeiter";
    return true;
  });

  // Zusätzlich nach Suchbegriff (Firma, Name, E-Mail) filtern.
  const q = search.trim().toLowerCase();
  const searched = q
    ? filteredMembers.filter((m) =>
        [m.company, m.name, m.email].some((v) => (v || "").toLowerCase().includes(q))
      )
    : filteredMembers;

  // Mandanten-Ansicht (Steuerberater): Mandanten als aufklappbare Ordner mit ihren Mitarbeitern.
  const mitarbeiterByMandant = {};
  for (const m of members) {
    if (m.account_type === "mitarbeiter" && m.parent_id) {
      (mitarbeiterByMandant[m.parent_id] ||= []).push(m);
    }
  }
  const [openMandanten, setOpenMandanten] = useState(() => new Set());
  const toggleMandant = (id) =>
    setOpenMandanten((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Einheitliche Personenzeile: Firmenname zuerst, dann Name; Mail- & Öffnen-Icon.
  const personRow = (m, { depth = 0, expandable = false, isOpen = false, onToggle = null, extra = null } = {}) => (
    <div
      key={m.id}
      className={`flex flex-wrap items-center gap-3 px-5 py-3 ${depth ? "bg-muted/30" : ""}`}
      style={depth ? { paddingLeft: 44 } : undefined}
      data-testid={depth ? "member-subrow" : "member-row"}
    >
      {expandable ? (
        <button
          type="button"
          onClick={onToggle}
          className="shrink-0 h-5 w-5 inline-flex items-center justify-center rounded hover:bg-muted text-muted-foreground"
          data-testid={`member-toggle-${m.id}`}
          aria-expanded={isOpen}
          title={isOpen ? "Zuklappen" : "Aufklappen"}
        >
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
      ) : (
        <span className="shrink-0 w-5" />
      )}
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{accountLabel(m)}</div>
      </div>
      <span className="shrink-0">
        <PlanRoleBadges plan={m.plan} rolle={m.rolle} />
      </span>
      {!m.active && (
        <span className="rounded-full bg-red-50 text-red-600 px-2 py-0.5 text-[10px] font-semibold shrink-0 cursor-default select-none">
          DEAKTIV
        </span>
      )}
      {extra}
      <a
        href={`mailto:${m.email}`}
        title={`E-Mail an ${m.email} schreiben`}
        data-testid={`mail-${m.id}`}
        className="shrink-0 h-9 w-9 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
      >
        <Mail className="h-4 w-4" />
      </a>
      <Button
        variant="ghost"
        size="icon"
        className="shrink-0"
        disabled={!m.active || openingId === m.id}
        onClick={() => openMember(m.id)}
        title="Konto ansehen / öffnen"
        data-testid={`open-account-${m.id}`}
      >
        {openingId === m.id ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Eye className="h-4 w-4" />
        )}
      </Button>
    </div>
  );

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim() || !role) {
      toast.error("Bitte E-Mail und Rolle angeben.");
      return;
    }
    setBusy(true);
    try {
      const res = await orgCreateInvitation(email.trim(), role);
      if (res.status === "consent_pending")
        toast.success("Bestehender Kunde – Zustimmungs-E-Mail wurde gesendet.");
      else toast.success("Einladung wurde gesendet.");
      setEmail("");
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Einladung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      await orgRevokeInvitation(id);
      await load();
      toast.success("Einladung zurückgezogen.");
    } catch {
      toast.error("Zurückziehen fehlgeschlagen.");
    }
  };

  const resend = async (id) => {
    setResendId(id);
    try {
      await orgResendInvitation(id);
      toast.success("Einladung erneut versendet.");
    } catch {
      toast.error("Erneutes Versenden fehlgeschlagen.");
    } finally {
      setResendId(null);
    }
  };

  const openMember = async (id) => {
    setOpeningId(id);
    try {
      await openAccount(id);
      navigate("/");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Konto konnte nicht geöffnet werden.");
      setOpeningId(null);
    }
  };

  const applyPerm = (m, res) =>
    setMembers((prev) =>
      prev.map((x) =>
        x.id === m.id
          ? { ...x, permissions: res.permissions, rolle: res.rolle ?? x.rolle }
          : x
      )
    );

  const togglePermission = async (m, next) => {
    try {
      const res = await orgSetMemberPermission(m.id, { view_others: next });
      applyPerm(m, res);
      toast.success(
        next ? "Einsicht in andere Konten aktiviert." : "Berechtigung entzogen."
      );
    } catch {
      toast.error("Berechtigung konnte nicht geändert werden.");
    }
  };

  const toggleAdmin = async (m, next) => {
    try {
      const res = await orgSetMemberPermission(m.id, { admin: next });
      applyPerm(m, res);
      toast.success(next ? "Admin-Recht erteilt." : "Admin-Recht entzogen.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Admin-Recht konnte nicht geändert werden.");
    }
  };

  // Kanzlei-Mitarbeiter: „Nur Mandanten bearbeiten" (Design analog zu Mitarbeiter-Rechten).
  const toggleEditMandanten = async (m, next) => {
    try {
      const res = await orgSetMemberPermission(m.id, { edit_mandanten: next });
      applyPerm(m, res);
      toast.success(
        next ? "Recht „Nur Mandanten bearbeiten“ aktiviert." : "Berechtigung entzogen."
      );
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Berechtigung konnte nicht geändert werden.");
    }
  };

  const doDecouple = async () => {
    setDecoupling(true);
    try {
      await orgDecouple();
      await refresh();
      toast.success("Verbindung zur Kanzlei getrennt.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Trennen fehlgeschlagen.");
    } finally {
      setDecoupling(false);
    }
  };

  const viewTitle =
    ansicht === "mandanten" ? "Mandanten" : ansicht === "mitarbeiter" ? "Mitarbeiter" : null;
  const title = viewTitle || (isViewer ? "Konten" : isKanzlei ? "Kanzlei" : "Mein Team");
  const subtitle = isViewer
    ? "Konten, die Sie einsehen dürfen. Öffnen Sie ein Konto, um dessen Belege zu sehen."
    : ansicht === "mandanten"
    ? "Laden Sie Mandanten (Unternehmer) ein und verwalten Sie deren Konten."
    : ansicht === "mitarbeiter"
    ? "Laden Sie Mitarbeiter ein und verwalten Sie deren Konten."
    : isKanzlei
    ? "Laden Sie Mandanten und Kanzlei-Mitarbeiter ein und verwalten Sie deren Konten."
    : "Laden Sie Mitarbeiter ein und verwalten Sie deren Konten.";
  const membersLabel = viewTitle
    ? viewTitle
    : isViewer
    ? "Einsehbare Konten"
    : isKanzlei
    ? "Mandanten & Mitarbeiter"
    : "Mitarbeiter";

  return (
    <div className="space-y-8" data-testid="organization-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">{title}</h1>
        <p className="text-muted-foreground mt-1">{subtitle}</p>
      </div>

      {/* Entkopplung (nur für mit einer Kanzlei verknüpfte Unternehmer) */}
      {isCoupled && (
        <div className="border border-destructive/30 bg-destructive/5 p-5" data-testid="decouple-card">
          <div className="flex items-center gap-2 mb-2">
            <Unlink className="h-5 w-5 text-destructive" />
            <h2 className="font-medium">Verbindung zur Kanzlei</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Ihr Konto wird derzeit von einer Steuerberatungskanzlei verwaltet und abgerechnet.
            Wenn Sie die Verbindung trennen, verliert die Kanzlei den Zugriff auf Ihr Konto und
            Sie verwalten und bezahlen Ihr Konto ab sofort eigenständig.
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="secondary"
                className="border border-destructive/40 text-destructive hover:bg-destructive hover:text-white gap-2"
                data-testid="decouple-button"
              >
                <Unlink className="h-4 w-4" /> Verbindung zur Kanzlei trennen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="decouple-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Verbindung wirklich trennen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Die Kanzlei verliert sofort den vollständigen Zugriff auf Ihr Konto und wird
                  per E-Mail informiert. Ihre Mitarbeiter bleiben Ihnen zugeordnet. Diese Aktion
                  kann nur durch eine neue Einladung der Kanzlei rückgängig gemacht werden.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="decouple-cancel">Abbrechen</AlertDialogCancel>
                <AlertDialogAction
                  onClick={doDecouple}
                  disabled={decoupling}
                  className="bg-destructive hover:bg-destructive/90"
                  data-testid="decouple-confirm"
                >
                  {decoupling ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ja, trennen"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {/* Einladung (nur für Kanzlei / Unternehmer) */}
      {canInvite && (
        <div className="border border-border bg-white p-5">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus className="h-5 w-5 text-primary" />
            <h2 className="font-medium">Person einladen</h2>
          </div>
          <form
            onSubmit={invite}
            className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end"
            data-testid="invite-form"
          >
            <div className="space-y-2">
              <Label htmlFor="inv-email">E-Mail-Adresse</Label>
              <Input
                id="inv-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@firma.de"
                className="bg-white"
                data-testid="invite-email"
              />
            </div>
            <Button type="submit" disabled={busy} className="gap-2" data-testid="invite-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Einladen
            </Button>
          </form>
        </div>
      )}

      {/* Offene Einladungen (nur für Kanzlei / Unternehmer) */}
      {canInvite && (
        <div className="border border-border bg-white">
          <div className="px-5 py-3 border-b border-border font-medium">
            Offene Einladungen{" "}
            <span className="text-sm text-muted-foreground">({invites.length})</span>
          </div>
          {loading ? (
            <div className="p-5 text-sm text-muted-foreground">Lädt…</div>
          ) : invites.length === 0 ? (
            <div className="p-5 text-sm text-muted-foreground" data-testid="invites-empty">
              Keine offenen Einladungen.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {invites.map((inv) => (
                <div
                  key={inv.id}
                  className="flex items-center gap-3 px-5 py-3"
                  data-testid="invitation-row"
                >
                  <span
                    className="shrink-0 h-9 w-9 inline-flex items-center justify-center rounded-md bg-muted text-muted-foreground"
                    aria-hidden="true"
                  >
                    <Clock className="h-4 w-4" />
                  </span>
                  <div className="flex-1 min-w-0 flex items-center gap-3 flex-wrap">
                    <span className="font-medium truncate">{inv.email}</span>
                    {inv.role_label && (
                      <span className="rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap">
                        {inv.role_label}
                      </span>
                    )}
                    {inv.created_at && (
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        eingeladen am{" "}
                        {new Date(inv.created_at).toLocaleDateString("de-DE", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })}
                      </span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => resend(inv.id)}
                    disabled={resendId === inv.id}
                    title="Einladung erneut senden"
                    data-testid={`resend-invitation-${inv.id}`}
                  >
                    {resendId === inv.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => revoke(inv.id)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    title="Zurückziehen"
                    data-testid={`revoke-invitation-${inv.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mitglieder / einsehbare Konten */}
      <div className="border border-border bg-white">
        <div className="px-5 py-3 border-b border-border font-medium flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            {membersLabel}{" "}
            <span className="text-sm text-muted-foreground">({searched.length})</span>
          </span>
          {members.length > 0 && (
            <div className="relative ml-auto w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Suche: Firma, Name, E-Mail"
                className="pl-9 bg-white rounded-none h-9"
                data-testid="member-search"
              />
            </div>
          )}
        </div>
        {loading ? (
          <div className="p-5 text-sm text-muted-foreground">Lädt…</div>
        ) : searched.length === 0 ? (
          <div className="p-5 text-sm text-muted-foreground" data-testid="members-empty">
            {q
              ? "Keine Treffer für Ihre Suche."
              : isViewer
              ? "Keine Konten freigegeben."
              : "Noch keine verknüpften Konten."}
          </div>
        ) : ansicht === "mandanten" ? (
          <div className="divide-y divide-border">
            {searched.map((m) => {
              const kids = mitarbeiterByMandant[m.id] || [];
              const isOpen = openMandanten.has(m.id);
              return (
                <div key={m.id}>
                  {personRow(m, {
                    expandable: kids.length > 0,
                    isOpen,
                    onToggle: () => toggleMandant(m.id),
                  })}
                  {isOpen && kids.map((k) => personRow(k, { depth: 1 }))}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="divide-y divide-border">
            {searched.map((m) => {
              const hasViewOthers = (m.permissions || []).includes("view_others");
              const hasAdmin = (m.permissions || []).includes("admin");
              const hasEditMandanten = (m.permissions || []).includes("edit_mandanten");
              const canTogglePerm = isUnternehmer && m.account_type === "mitarbeiter";
              // Mitarbeiter-Admin (Unternehmer-Team) – wie bisher.
              const canToggleMitAdmin =
                isOwner && user?.account_type === "unternehmer" && m.account_type === "mitarbeiter";
              // Kanzlei-Mitarbeiter: Rechte via Switches (Steuerberater ODER Kanzlei-Admin, nicht man selbst).
              const canSetKanzleiLevel =
                m.account_type === "kanzlei_mitarbeiter" &&
                m.id !== user?.id &&
                (user?.account_type === "steuerberater" || isKanzleiAdmin);
              const extra = (
                <>
                  {canToggleMitAdmin && (
                    <label
                      className="flex items-center gap-2 text-xs text-muted-foreground shrink-0"
                      title="Admin-Rechte: Team verwalten & Mitarbeiter einladen (Abrechnung & Löschen bleiben beim Hauptbesitzer)"
                    >
                      <Switch
                        checked={hasAdmin}
                        onCheckedChange={(v) => toggleAdmin(m, v)}
                        data-testid={`perm-admin-${m.id}`}
                      />
                      Admin-Rechte
                    </label>
                  )}
                  {canTogglePerm && (
                    <label
                      className="flex items-center gap-2 text-xs text-muted-foreground shrink-0"
                      title="Erlaubt diesem Mitarbeiter, andere Konten einzusehen"
                    >
                      <Switch
                        checked={hasViewOthers}
                        disabled={hasAdmin}
                        onCheckedChange={(v) => togglePermission(m, v)}
                        data-testid={`perm-view-others-${m.id}`}
                      />
                      Einsicht in andere Konten
                    </label>
                  )}
                  {canSetKanzleiLevel && (
                    <>
                      <label
                        className="flex items-center gap-2 text-xs text-muted-foreground shrink-0"
                        title="Admin-Rechte: Mandanten & Kanzlei-Mitarbeiter einladen und verwalten, alle Konten öffnen"
                      >
                        <Switch
                          checked={hasAdmin}
                          onCheckedChange={(v) => toggleAdmin(m, v)}
                          data-testid={`perm-admin-${m.id}`}
                        />
                        Admin-Rechte
                      </label>
                      <label
                        className="flex items-center gap-2 text-xs text-muted-foreground shrink-0"
                        title="Erlaubt, alle Mandanten der Kanzlei zu öffnen und deren Belege zu bearbeiten"
                      >
                        <Switch
                          checked={hasEditMandanten}
                          disabled={hasAdmin}
                          onCheckedChange={(v) => toggleEditMandanten(m, v)}
                          data-testid={`perm-edit-mandanten-${m.id}`}
                        />
                        Nur Mandanten bearbeiten
                      </label>
                    </>
                  )}
                </>
              );
              return personRow(m, { extra });
            })}
          </div>
        )}
      </div>
    </div>
  );
}
